import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Modal } from "../components/Modal";
import { SchoolPicker } from "../components/SchoolPicker";

import { searchParam, useDebouncedValue } from "./admin/shared";

import type { ApiErrorCode } from "../api/client";
import type { School, Teacher } from "@canvaschool/contracts";

/**
 * Administrators manage teacher accounts here, and enter an active teacher's
 * account (with every right that teacher has) by choosing their ID.
 */
export const AdminTeachersPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [teachers, setTeachers] = useState<Teacher[] | null>(null);
  const [loginId, setLoginId] = useState("");
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [resetTarget, setResetTarget] = useState<Teacher | null>(null);
  const [stopTarget, setStopTarget] = useState<Teacher | null>(null);
  const [schoolTarget, setSchoolTarget] = useState<Teacher | null>(null);
  const [schoolPick, setSchoolPick] = useState<School | null>(null);
  const [schoolError, setSchoolError] = useState<ApiErrorCode | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const q = searchParam(useDebouncedValue(search));
  const qRef = useRef(q);
  qRef.current = q;
  /** Only the newest request may write the list. */
  const requestRef = useRef(0);

  const load = useCallback(
    async (clearError = false) => {
      const ticket = ++requestRef.current;
      if (clearError) {
        setError(null);
      }
      try {
        const result = await api.listTeachers({ q: qRef.current });
        if (ticket === requestRef.current) {
          setTeachers(result.items);
          setNextCursor(result.nextCursor);
        }
      } catch (failure) {
        if (ticket === requestRef.current) {
          setError(auth.handleError(failure));
        }
      }
    },
    [api, auth],
  );

  useEffect(() => {
    void load(true);
    // Reloading per search; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, q]);

  const loadMore = async () => {
    if (!nextCursor || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    // A search change takes a newer ticket, which voids this request.
    const ticket = ++requestRef.current;
    try {
      const result = await api.listTeachers({ q, cursor: nextCursor });
      if (ticket === requestRef.current) {
        setTeachers((current) => [...(current ?? []), ...result.items]);
        setNextCursor(result.nextCursor);
      }
    } catch (failure) {
      if (ticket === requestRef.current) {
        setError(auth.handleError(failure));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  /** One account action at a time, so a double click never repeats it. */
  const run = async (action: () => Promise<unknown>) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
      setResetTarget(null);
      setStopTarget(null);
      await load();
    } catch (failure) {
      setError(auth.handleError(failure));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  /** The shell follows the new teacher session to the teacher's home. */
  const enter = async (teacher: Teacher) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await auth.impersonate(teacher.id);
    } catch (failure) {
      setError(auth.handleError(failure));
      busyRef.current = false;
      setBusy(false);
    }
  };

  const saveSchool = async () => {
    if (!schoolTarget || !schoolPick || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setSchoolError(null);
    try {
      const updated = await api.setTeacherSchool(
        schoolTarget.id,
        schoolPick.code,
      );
      setTeachers((current) =>
        (current ?? []).map((item) =>
          item.id === updated.id ? updated : item,
        ),
      );
      setSchoolTarget(null);
    } catch (failure) {
      setSchoolError(auth.handleError(failure));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>교사 계정</h1>
        </div>
      </div>
      <form
        aria-label="교사 추가"
        className="canvaschool-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          void run(async () => {
            await api.createTeacher(loginId.trim());
            setLoginId("");
          });
        }}
      >
        <label htmlFor="canvaschool-teacher-login">교사 아이디</label>
        <input
          id="canvaschool-teacher-login"
          value={loginId}
          onChange={(event) => setLoginId(event.target.value)}
          required
        />
        <button
          type="submit"
          className="canvaschool-btn-primary"
          disabled={busy}
        >
          교사 추가
        </button>
      </form>
      <p className="canvaschool-hint">
        새 교사의 초기 비밀번호는 12345678입니다. 비밀번호를 초기화하면
        12345678로 되돌아갑니다. 사용 중인 교사의 아이디를 누르면 그 선생님의
        화면으로 들어가 학급을 똑같이 다룰 수 있습니다.
      </p>
      <div className="canvaschool-inline-form">
        <label htmlFor="canvaschool-teacher-search">교사 검색</label>
        <input
          id="canvaschool-teacher-search"
          type="search"
          value={search}
          placeholder="아이디, 이메일"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {error && <ErrorAlert code={error} />}
      {teachers && teachers.length === 0 && (
        <p className="canvaschool-hint">조건에 맞는 교사가 없습니다.</p>
      )}
      {teachers && teachers.length > 0 && (
        <div className="canvaschool-table-wrap">
          <table className="canvaschool-table">
            <thead>
              <tr>
                <th scope="col">아이디</th>
                <th scope="col">이메일</th>
                <th scope="col">학교</th>
                <th scope="col">상태</th>
                <th scope="col">비밀번호</th>
                <th scope="col">관리</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((teacher) => (
                <tr key={teacher.id}>
                  <td className="canvaschool-prefix">
                    {teacher.status === "ACTIVE" ? (
                      <button
                        type="button"
                        className="canvaschool-link-button"
                        aria-label={`${teacher.loginId} 선생님 화면으로 들어가기`}
                        disabled={busy}
                        onClick={() => void enter(teacher)}
                      >
                        {teacher.loginId}
                      </button>
                    ) : (
                      teacher.loginId
                    )}
                  </td>
                  <td>{teacher.email ?? "—"}</td>
                  <td>
                    {teacher.school ? (
                      <>
                        {teacher.school.name}
                        <small className="canvaschool-school-sub">
                          {teacher.school.officeName}
                        </small>
                      </>
                    ) : teacher.applicationSchoolName ? (
                      <span className="canvaschool-hint">
                        {teacher.applicationSchoolName} (미연결)
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <span
                      className={`canvaschool-status ${
                        teacher.status === "ACTIVE"
                          ? "canvaschool-status--on"
                          : "canvaschool-status--off"
                      }`}
                    >
                      {teacher.status === "ACTIVE" ? "사용 중" : "사용 중지됨"}
                    </span>
                  </td>
                  <td>
                    {teacher.initialPasswordActive ? "초기 비밀번호" : "변경됨"}
                  </td>
                  <td>
                    <div className="canvaschool-actions">
                      <button
                        type="button"
                        aria-label={`${teacher.loginId} 학교 ${
                          teacher.school ? "변경" : "연결"
                        }`}
                        onClick={() => {
                          setSchoolPick(teacher.school);
                          setSchoolError(null);
                          setSchoolTarget(teacher);
                        }}
                      >
                        {teacher.school ? "변경" : "학교 연결"}
                      </button>
                      <button
                        type="button"
                        className="canvaschool-btn-danger"
                        aria-label={`${teacher.loginId} ${
                          teacher.status === "ACTIVE"
                            ? "사용 중지"
                            : "다시 사용"
                        }`}
                        onClick={() =>
                          teacher.status === "ACTIVE"
                            ? setStopTarget(teacher)
                            : void run(() =>
                                api.setTeacherStatus(teacher.id, "ACTIVE"),
                              )
                        }
                      >
                        {teacher.status === "ACTIVE"
                          ? "사용 중지"
                          : "다시 사용"}
                      </button>
                      <button
                        type="button"
                        className="canvaschool-btn-danger"
                        aria-label={`${teacher.loginId} 비밀번호 초기화`}
                        onClick={() => setResetTarget(teacher)}
                      >
                        비밀번호 초기화
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && (
        <button type="button" onClick={() => void loadMore()} disabled={busy}>
          더 보기
        </button>
      )}
      {schoolTarget && (
        <Modal
          title={schoolTarget.school ? "학교 변경" : "학교 연결"}
          primaryLabel="저장"
          pending={busy}
          primaryDisabled={!schoolPick}
          onCancel={() => setSchoolTarget(null)}
          onPrimary={() => void saveSchool()}
        >
          <div className="canvaschool-modal-info-box">
            <p>
              <strong>{schoolTarget.loginId}</strong> 선생님의 학교를 고릅니다.
            </p>
            {schoolTarget.applicationSchoolName && (
              <p className="canvaschool-hint">
                신청서에 적은 학교: {schoolTarget.applicationSchoolName}
              </p>
            )}
            <div className="canvaschool-field">
              <label htmlFor="canvaschool-admin-school">학교</label>
              <SchoolPicker
                id="canvaschool-admin-school"
                value={schoolPick}
                onChange={setSchoolPick}
                initialQuery={schoolTarget.applicationSchoolName ?? ""}
                disabled={busy}
                autoFocus
              />
            </div>
            {schoolError && <ErrorAlert code={schoolError} />}
          </div>
        </Modal>
      )}
      {stopTarget && (
        <Modal
          title="사용 중지"
          primaryLabel="사용 중지"
          danger
          pending={busy}
          onCancel={() => setStopTarget(null)}
          onPrimary={() =>
            void run(() => api.setTeacherStatus(stopTarget.id, "DISABLED"))
          }
        >
          <div className="canvaschool-modal-info-box">
            <p>
              <strong>{stopTarget.loginId}</strong> 선생님의 계정을 사용
              중지합니다. 로그인할 수 없게 됩니다.
            </p>
          </div>
        </Modal>
      )}
      {resetTarget && (
        <Modal
          title="비밀번호 초기화"
          primaryLabel="초기화"
          pending={busy}
          onCancel={() => setResetTarget(null)}
          onPrimary={() =>
            void run(() => api.resetTeacherPassword(resetTarget.id))
          }
        >
          <div className="canvaschool-modal-info-box">
            <p>
              <strong>{resetTarget.loginId}</strong> 선생님의 비밀번호를 초기
              비밀번호 <strong>12345678</strong>로 되돌립니다.
            </p>
            <p className="canvaschool-hint">
              초기화 후 선생님께 변경된 초기 비밀번호를 안내해 주세요.
            </p>
          </div>
        </Modal>
      )}
    </section>
  );
};
